import { useEffect, useState } from "react";
import { useAccount } from "../../controllers/useAccount";
import type { BillingPeriod, SubscriptionTier } from "../../models/account";

const TIER_LABEL: Record<SubscriptionTier, string> = { FREE: "FREE", SPECIAL: "SPECIAL" };
const PERIOD_LABEL: Record<BillingPeriod, string> = { NONE: "-", MONTHLY: "월간", YEARLY: "연간" };
const EVENT_LABEL: Record<string, string> = {
  PROVISIONED: "생성", RENEWED: "갱신", UPGRADED: "업그레이드", DOWNGRADED: "다운그레이드",
  EXPIRED: "만료", CANCELED: "해지",
};

function fmt(d?: string | null) {
  if (!d) return "-";
  return new Date(d).toLocaleString();
}

// Current subscription + append-only change history + a change-tier/cancel control. Payment is
// deferred, so change-tier here only flips the plan record — it is clearly labeled as such.
export function SubscriptionView() {
  const acc = useAccount();
  const [tier, setTier] = useState<SubscriptionTier>("FREE");
  const [period, setPeriod] = useState<BillingPeriod>("MONTHLY");
  const [seats, setSeats] = useState<number | "">("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void acc.loadHistory();
    if (acc.subscription) {
      setTier(acc.subscription.tier);
      setPeriod(acc.subscription.billingPeriod === "NONE" ? "MONTHLY" : acc.subscription.billingPeriod);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!acc.subscription) return <div className="center">불러오는 중…</div>;
  const sub = acc.subscription;

  async function submitChange() {
    setBusy(true);
    try {
      await acc.changeSubscriptionTier({ tier, billingPeriod: period, seats: seats === "" ? undefined : seats });
    } finally {
      setBusy(false);
    }
  }

  async function submitCancel() {
    if (!window.confirm("구독을 해지하시겠습니까?")) return;
    setBusy(true);
    try {
      await acc.cancelMySubscription();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="detail-body">
      <div className="detail-main">
        <div className="info-card" style={{ marginBottom: 16 }}>
          <h4>현재 구독</h4>
          <div className="kv"><span>플랜</span><span>{TIER_LABEL[sub.tier]}</span></div>
          <div className="kv"><span>결제 주기</span><span>{PERIOD_LABEL[sub.billingPeriod]}</span></div>
          <div className="kv"><span>상태</span><span>{sub.status}</span></div>
          <div className="kv"><span>현재 기간</span><span>{fmt(sub.currentPeriodStart)} ~ {fmt(sub.currentPeriodEnd)}</span></div>
          <div className="kv"><span>자동 갱신</span><span>{sub.autoRenew ? "예" : "아니오"}</span></div>
          {sub.seats != null && <div className="kv"><span>시트 수</span><span>{sub.seats}</span></div>}
        </div>

        <div className="info-card" style={{ marginBottom: 16 }}>
          <h4>플랜 변경</h4>
          <div className="note" style={{ marginBottom: 14 }}>
            <b>결제 연동 전 (Mock)</b><br />
            아직 결제(PG) 연동이 되어있지 않습니다. 아래 변경은 플랜 레코드만 갱신하며 실제 과금은 발생하지 않습니다.
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ display: "flex", gap: 14 }}>
              <label className="sdui-field" style={{ flex: 1 }}>
                <span className="sdui-label">플랜</span>
                <select className="sdui-input" value={tier} onChange={(e) => setTier(e.target.value as SubscriptionTier)}>
                  <option value="FREE">FREE</option>
                  <option value="SPECIAL">SPECIAL</option>
                </select>
              </label>
              <label className="sdui-field" style={{ flex: 1 }}>
                <span className="sdui-label">결제 주기</span>
                <select className="sdui-input" value={period} onChange={(e) => setPeriod(e.target.value as BillingPeriod)}>
                  <option value="MONTHLY">월간</option>
                  <option value="YEARLY">연간</option>
                </select>
              </label>
            </div>
            {tier === "SPECIAL" && (
              <label className="sdui-field">
                <span className="sdui-label">시트 수 (선택)</span>
                <input
                  className="sdui-input" type="number" min={1}
                  value={seats}
                  onChange={(e) => setSeats(e.target.value === "" ? "" : Number(e.target.value))}
                />
              </label>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button className="btn ghost" style={{ width: "auto" }} disabled={busy || sub.status !== "ACTIVE"} onClick={submitCancel}>구독 해지</button>
              <button className="btn primary" style={{ width: "auto" }} disabled={busy} onClick={submitChange}>
                {busy ? "처리 중…" : "플랜 변경 (Mock)"}
              </button>
            </div>
          </div>
        </div>

        <div className="info-card">
          <h4>변경 이력</h4>
          <table className="sdui-table">
            <thead>
              <tr><th>종류</th><th>변경</th><th>결제 주기</th><th>적용일</th><th>메모</th></tr>
            </thead>
            <tbody>
              {acc.history.map((h, i) => (
                <tr key={i}>
                  <td>{EVENT_LABEL[h.type] ?? h.type}</td>
                  <td>{h.fromTier ?? "-"} → {h.toTier ?? "-"}</td>
                  <td>{h.billingPeriod ? PERIOD_LABEL[h.billingPeriod] : "-"}</td>
                  <td>{fmt(h.effectiveAt)}</td>
                  <td>{h.note ?? "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {acc.history.length === 0 && <div className="sdui-table-empty">변경 이력이 없습니다.</div>}
        </div>
      </div>
    </div>
  );
}
